/**
 * 通用格式化工具（以 web/admin/src/lib/format.ts 为底本迁移）。
 *
 * 与底本差异：删除 statusBadge 的 POSITIVE/NEGATIVE/PENDING 手工集合——
 * 状态 → 色彩的职责已移交 dictionaries（显式 tone）+ semanticTone（启发式兜底）。
 */
import { currencyFractionDigits } from './decimal.ts';

export type FormatMoneyOptions = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

/**
 * 千分位金额展示（币种感知）。
 *
 * - fiat（币种表见 decimal.ts 单一真源）默认固定 2 位；非 fiat 默认 0~8 位并去尾零；
 * - 显式 opts 优先于币种默认值；
 * - 非法输入原样返回（字符串）或「—」（非有限数字），绝不输出 `NaN`；
 * - 展示层允许 Number 转换（金额禁 double 仅约束领域/业务真值路径）。
 */
export function formatMoney(
  v: string | number,
  currency?: string,
  opts?: FormatMoneyOptions,
): string {
  if (typeof v === 'number' && !Number.isFinite(v)) {
    return '—';
  }
  const source = typeof v === 'number' ? String(v) : String(v ?? '').trim();
  if (!/^-?\d+(\.\d+)?$/.test(source)) {
    return typeof v === 'string' ? v : source;
  }
  const code = String(currency ?? '').trim().toUpperCase() || 'USD';
  const scale = currencyFractionDigits(code);
  const minimumFractionDigits = opts?.minimumFractionDigits ?? (scale === 2 ? 2 : 0);
  const maximumFractionDigits =
    opts?.maximumFractionDigits ?? Math.max(minimumFractionDigits, scale);
  return `${code} ${Number(source).toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  })}`;
}

/** 简化日期时间展示（YYYY-MM-DD HH:mm）；非法输入原样返回 */
export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 仅日期（YYYY-MM-DD） */
export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return formatDateTime(iso).slice(0, 10);
}

/** 本地时区日期时间（入参 Date）：Y-M-D sep H:m[:s]，各段补零（FE1-029 收口 admin 手写 pad+getFullYear）。
 * sep='T'（datetime-local 输入值）/ ' '（后台展示）可选；seconds=false 时止于分钟。 */
export function formatLocalDateTime(
  d: Date = new Date(),
  opts?: { sep?: 'T' | ' '; seconds?: boolean },
): string {
  const sep = opts?.sep ?? ' ';
  const pad = (x: number) => String(x).padStart(2, '0');
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (opts?.seconds === false) {
    return `${date}${sep}${hm}`;
  }
  return `${date}${sep}${hm}:${pad(d.getSeconds())}`;
}

/** 本地时区纯日期（YYYY-MM-DD，各段补零） */
export function formatLocalDate(d: Date = new Date()): string {
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 百分比格式化（带正负号，用于环比/趋势变化） */
export function formatPercent(v: number, withSign = true): string {
  if (Number.isNaN(v)) return '—';
  const sign = withSign && v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
}
